const TRAIN_TRANSIT_TYPES = new Set([
    'HEAVY_RAIL', 'COMMUTER_TRAIN', 'HIGH_SPEED_TRAIN', 'LONG_DISTANCE_TRAIN',
    'METRO_RAIL', 'MONORAIL', 'RAIL', 'SUBWAY', 'TRAM'
]);

let mapsLoaderPromise = null;

export function routesEnabled() {
    const value = String(process.env.REACT_APP_GOOGLE_ROUTES_ENABLED || '0').trim().toLowerCase();
    if (!['0', '1', 'false', 'true'].includes(value)) {
        throw new Error('REACT_APP_GOOGLE_ROUTES_ENABLED must be 0, 1, false or true.');
    }
    return value === '1' || value === 'true';
}

export function loadGoogleMapsApi(legacyKey) {
    const configuredKey = String(process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '').trim();
    const modern = routesEnabled();
    if (configuredKey && (configuredKey.includes('$(') || ['undefined', 'null'].includes(configuredKey.toLowerCase()))) {
        return Promise.reject(new Error('The browser Google Maps key is not configured correctly.'));
    }
    if (modern && !configuredKey) {
        return Promise.reject(new Error('Configure REACT_APP_GOOGLE_MAPS_API_KEY before enabling Google Routes.'));
    }
    const key = configuredKey || legacyKey;
    if (!key) return Promise.reject(new Error('The browser Google Maps key is missing.'));

    const existing = document.querySelector('script[data-ms-google-maps="1"]');
    if (configuredKey && existing && new URL(existing.src, window.location.href).searchParams.get('key') !== configuredKey) {
        return Promise.reject(new Error('Google Maps configuration changed. Reload the page before calculating routes.'));
    }
    if (window.google?.maps) return Promise.resolve(window.google.maps);
    if (mapsLoaderPromise) return mapsLoaderPromise;

    mapsLoaderPromise = new Promise((resolve, reject) => {
        const script = existing || document.createElement('script');
        const callbackName = '__monsurvGoogleMapsReady';
        let timer;
        const cleanup = () => {
            clearTimeout(timer);
            script.removeEventListener('load', onLoad);
            script.removeEventListener('error', onError);
            if (!existing) delete window[callbackName];
        };
        const ready = () => {
            if (!window.google?.maps) return;
            cleanup();
            resolve(window.google.maps);
        };
        const fail = (message) => {
            cleanup();
            if (!existing) script.remove();
            reject(new Error(message));
        };
        function onLoad() { ready(); }
        function onError() { fail('Google Maps failed to load. Check the browser key and API restrictions.'); }
        script.addEventListener('load', onLoad);
        script.addEventListener('error', onError);
        timer = setTimeout(() => fail('Google Maps loading timed out. Check connectivity and API configuration.'), 30000);

        if (!existing) {
            window[callbackName] = ready;
            script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&v=weekly&loading=async&callback=${callbackName}`;
            script.async = true;
            script.defer = true;
            script.setAttribute('data-ms-google-maps', '1');
            document.head.appendChild(script);
        }
    }).catch((error) => {
        mapsLoaderPromise = null;
        throw error;
    });
    return mapsLoaderPromise;
}

function transitDetails(step) {
    return step?.transitDetails || step?.transit || step?.transit_details || null;
}

export function isTrainTransitStep(step) {
    const details = transitDetails(step);
    const vehicle = details?.transitLine?.vehicle || details?.line?.vehicle;
    return TRAIN_TRANSIT_TYPES.has(String(vehicle?.vehicleType || vehicle?.type || vehicle?.name || '').trim().toUpperCase());
}

export function isTrainOnlyRoute(result) {
    return result.transitSteps.length > 0 && result.transitSteps.every(isTrainTransitStep);
}

function waypoint(value) {
    if (typeof value === 'string') {
        const text = value.trim();
        if (!text) throw new Error('An origin and destination are required to calculate a route.');
        const coordinates = text.match(/^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)$/);
        if (coordinates) return waypoint({ lat: Number(coordinates[1]), lng: Number(coordinates[2]) });
        return text;
    }
    const lat = typeof value?.lat === 'function' ? value.lat() : value?.lat;
    const lng = typeof value?.lng === 'function' ? value.lng() : value?.lng;
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
        throw new Error('The route location must be an address or valid latitude/longitude.');
    }
    return { lat, lng };
}

function normalizeRoute({ nativeRoute, legacyResponse, distanceOnly, origin, destination }) {
    const leg = nativeRoute?.legs?.[0] || legacyResponse?.routes?.[0]?.legs?.[0];
    const distanceMeters = nativeRoute ? nativeRoute.distanceMeters : leg?.distance?.value;
    const durationMillis = nativeRoute ? nativeRoute.durationMillis : Number(leg?.duration?.value) * 1000;
    if (!Number.isFinite(distanceMeters) || distanceMeters < 0
        || (!distanceOnly && (!Number.isFinite(durationMillis) || durationMillis < 0))) {
        throw new Error('Google returned incomplete route distance or duration information.');
    }
    const steps = Array.isArray(leg?.steps) ? leg.steps : [];
    const transitSteps = steps.filter((step) => transitDetails(step)
        || String(step?.travelMode || step?.travel_mode || '').toUpperCase() === 'TRANSIT');
    const first = transitDetails(transitSteps[0]);
    const last = transitDetails(transitSteps[transitSteps.length - 1]);
    const localized = nativeRoute?.localizedValues;
    const minutes = Math.round(durationMillis / 60000);
    return {
        distanceMeters,
        distanceText: localized?.distance || leg?.distance?.text || `${(distanceMeters / 1609.344).toFixed(1)} mi`,
        durationText: distanceOnly ? '' : (localized?.duration || leg?.duration?.text
            || (minutes >= 60 ? `${Math.floor(minutes / 60)} hr ${minutes % 60} min` : `${minutes} min`)),
        departureStation: first?.departureStop?.name || first?.departure_stop?.name || leg?.start_address
            || (typeof origin === 'string' ? origin : ''),
        arrivalStation: last?.arrivalStop?.name || last?.arrival_stop?.name || leg?.end_address
            || (typeof destination === 'string' ? destination : ''),
        transitSteps,
        nativeRoute,
        legacyResponse
    };
}

export async function requestProjectRoute({ origin, destination, travelMode = 'driving', includePath = false, distanceOnly = false, legacyKey }) {
    const start = waypoint(origin);
    const end = waypoint(destination);
    if (!['driving', 'train', 'transit'].includes(travelMode)) throw new Error('Unsupported project travel mode.');
    const transit = travelMode !== 'driving';
    const maps = await loadGoogleMapsApi(legacyKey);
    if (routesEnabled()) {
        const { Route } = await maps.importLibrary('routes');
        const fields = distanceOnly ? ['distanceMeters'] : ['distanceMeters', 'durationMillis', 'localizedValues'];
        if (transit || includePath) fields.push('legs');
        if (includePath) fields.push('path');
        const response = await Route.computeRoutes({
            origin: start, destination: end,
            travelMode: transit ? 'TRANSIT' : 'DRIVING',
            fields, language: 'en-GB', region: 'gb', units: maps.UnitSystem?.IMPERIAL,
            ...(transit
                ? { departureTime: new Date(), transitPreference: { allowedTransitModes: ['TRAIN'] } }
                : { routingPreference: 'TRAFFIC_UNAWARE' })
        });
        const nativeRoute = response?.routes?.[0];
        if (!nativeRoute) throw new Error('No Google route was found for these locations.');
        return normalizeRoute({ nativeRoute, distanceOnly, origin: start, destination: end });
    }

    const service = new maps.DirectionsService();
    const request = {
        origin: start, destination: end,
        travelMode: transit ? maps.TravelMode.TRANSIT : maps.TravelMode.DRIVING,
        ...(transit && maps.TransitMode?.TRAIN
            ? { transitOptions: { departureTime: new Date(), modes: [maps.TransitMode.TRAIN] } } : {})
    };
    const legacyResponse = await new Promise((resolve, reject) => {
        service.route(request, (response, status) => {
            if (status === 'OK') resolve(response);
            else reject(new Error(`Google route unavailable: ${status || 'UNKNOWN_STATUS'}.`));
        });
    });
    return normalizeRoute({ legacyResponse, distanceOnly, origin: start, destination: end });
}

export function drawProjectRoute({ maps, map, result, polylineOptions }) {
    if (result.nativeRoute) {
        const path = result.nativeRoute.path;
        if (!Array.isArray(path) || !path.length) throw new Error('Google returned no route path to display.');
        const bounds = new maps.LatLngBounds();
        path.forEach((point) => bounds.extend(point));
        const polylines = result.nativeRoute.createPolylines({ polylineOptions });
        if (!polylines.length) throw new Error('Google returned no route lines to display.');
        try {
            polylines.forEach((polyline) => polyline.setMap(map));
        } catch (error) {
            polylines.forEach((polyline) => polyline.setMap(null));
            throw error;
        }
        return { bounds, setMap: (target) => polylines.forEach((polyline) => polyline.setMap(target)) };
    }
    const renderer = new maps.DirectionsRenderer({ suppressMarkers: true, preserveViewport: true, polylineOptions });
    renderer.setMap(map);
    renderer.setDirections(result.legacyResponse);
    return { bounds: result.legacyResponse?.routes?.[0]?.bounds, setMap: (target) => renderer.setMap(target) };
}
